import React from "react";

/**
 * Data-source freshness panel.
 *
 * For each configured data source, shows:
 *   - source type
 *   - relative time since last successful collection
 *   - a colored freshness dot (green = fresh, amber = stale, red = never / errored)
 *   - last error (agency view only)
 */
export default function DataSourceStatus({ sources, mode = "agency" }) {
  if (!sources || sources.length === 0) {
    return <div className="empty">No data sources configured.</div>;
  }
  return (
    <table className="data">
      <thead>
        <tr>
          <th></th>
          <th>Source</th>
          <th>Last collected</th>
          {mode === "agency" && <th>Notes</th>}
        </tr>
      </thead>
      <tbody>
        {sources.map((s) => {
          const fresh = freshnessBucket(s.last_collected_at, s.is_active, s.last_error);
          return (
            <tr key={s.id}>
              <td><span className={`dot dot-${fresh.level}`} title={fresh.label} /></td>
              <td>{prettySourceType(s.source_type)}</td>
              <td>
                {s.last_collected_at
                  ? <>{relativeTime(s.last_collected_at)} <span className="subtle">— {new Date(s.last_collected_at).toLocaleDateString()}</span></>
                  : <span className="subtle">never</span>}
              </td>
              {mode === "agency" && (
                <td>
                  {!s.is_active && <span className="subtle">inactive</span>}
                  {s.last_error && (
                    <span style={{ color: "var(--bad)", fontSize: 13 }}>{s.last_error}</span>
                  )}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// --- helpers ---

const LABELS = {
  gsc: "Google Search Console",
  ga4: "Google Analytics 4",
  pagespeed: "PageSpeed Insights",
  crawler: "Site crawler",
  geo: "AI / GEO baselining",
  gbp: "Google Business Profile",
  instagram: "Instagram",
  linkedin: "LinkedIn",
  facebook: "Facebook",
  email_mailchimp: "Mailchimp",
  email_constant_contact: "Constant Contact",
};

function prettySourceType(t) {
  return LABELS[t] || t;
}

function relativeTime(iso) {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = now - then;
  const min = 60 * 1000;
  const hr = 60 * min;
  const day = 24 * hr;
  if (diffMs < hr) return `${Math.max(1, Math.round(diffMs / min))} min ago`;
  if (diffMs < day) return `${Math.round(diffMs / hr)} hr ago`;
  const days = Math.round(diffMs / day);
  if (days < 30) return `${days} days ago`;
  const months = Math.round(days / 30);
  return `${months} mo ago`;
}

function freshnessBucket(lastCollectedAt, isActive, lastError) {
  if (!isActive) return { level: "off", label: "source inactive" };
  if (!lastCollectedAt) return { level: "bad", label: "never collected" };
  const ageMs = Date.now() - new Date(lastCollectedAt).getTime();
  const day = 24 * 60 * 60 * 1000;
  if (lastError) return { level: "bad", label: `error: ${lastError}` };
  if (ageMs < 2 * day) return { level: "good", label: "fresh" };
  if (ageMs < 10 * day) return { level: "warn", label: "stale" };
  return { level: "bad", label: "very stale" };
}
